<script lang="ts" setup>
import {computed, type HTMLAttributes, ref} from "vue";
import type {ResponsiveImage} from "@/shared/graphql/types";
import fallbackImage from '@/assets/placeholder.jpg';
import {faSpinnerThird} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

interface Props {
  image?: ResponsiveImage;
  placeholder?: ResponsiveImage;
  alt?: string;
  sizes?: string;
  class?: HTMLAttributes['class'];
}

const props = defineProps<Props>();

const placeHolderVisible = ref(props.image == null);
const loading = ref(true);

const createSrcSetByFormat = (image?: ResponsiveImage, format?: string): string => {
  if (!image) return '';
  return image.srcset
      .filter(src => format ? src.url.endsWith(`.${format}`) : !src.url.endsWith('.webp'))
      .map(src => `${src.url} ${src.width}w`)
      .join(",\n");
};

const webpSrcset = computed(() => {
  if (loading.value) return '';
  const source = placeHolderVisible.value ? props.placeholder : props.image;
  return createSrcSetByFormat(source, 'webp');
});

const fallbackSrcset = computed(() => {
  if (loading.value) return '';
  const source = placeHolderVisible.value ? props.placeholder : props.image;
  return createSrcSetByFormat(source);
});

const placeholderImage = computed(() => {
  return props.placeholder?.url ?? fallbackImage;
});

const currentSrc = ref(props.image?.url ?? placeholderImage.value);

const onImageError = () => {
  console.warn("Image failed to load", props.image?.url);
  placeHolderVisible.value = true;
  currentSrc.value = placeholderImage.value;
};
</script>

<template>
  <div v-if="loading" class="w-full h-full flex justify-center items-center min-h-12">
    <FontAwesomeIcon :icon="faSpinnerThird" spin size="lg" class="text-slate-300"/>
  </div>

  <!-- With sizes: use picture for format fallback -->
  <picture v-if="sizes && !loading">
    <source :srcset="webpSrcset" type="image/webp" :sizes="sizes"/>
    <source :srcset="fallbackSrcset" :sizes="sizes"/>
    <img
        :class="props.class"
        :alt="alt"
        :src="currentSrc"
        v-bind="$attrs"
        @load="loading = false"
        @error="onImageError"
    />
  </picture>

  <!-- Without sizes: fallback to direct <img> -->
  <img
      v-else
      :class="[props.class, { 'hidden': loading }]"
      :alt="alt"
      :src="currentSrc"
      v-bind="$attrs"
      @load="loading = false"
      @error="onImageError"
  />
</template>